import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, ArrowRight, MapPin, User, Leaf, ShoppingCart, Info } from 'lucide-react';

const VegetablesCatalogue = () => {
  const [selectedProduct, setSelectedProduct] = useState(null);

  const vegetables = [
    {
      id: 'potatoes',
      name: 'Premium Potatoes',
      subtitle: 'Grade A Table Potatoes',
      image: 'https://images.pexels.com/photos/144248/potatoes-vegetables-erdfrucht-bio-144248.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹25-35',
      unit: 'per kg',
      minOrder: '500 kg',
      season: 'Year-round',
      origin: 'Agra, Uttar Pradesh',
      organic: false,
      grade: 'Grade A',
      shelfLife: '2-3 months',
      description: 'Premium quality potatoes perfect for chips, fries, and cooking. Uniform size, low sugar content, ideal for processing and retail.',
      features: ['Low sugar', 'Uniform size', 'Long shelf life', 'Processing grade available'],
      farmer: {
        name: 'Ramesh Yadav',
        location: 'Agra, UP',
        image: 'https://images.pexels.com/photos/2804327/pexels-photo-2804327.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '20 years',
        farmSize: '100 acres',
        story: 'Our farms in the fertile plains of Agra produce some of the finest potatoes in India, supplied to major food processing companies.'
      },
      certifications: ['FSSAI', 'APEDA']
    },
    {
      id: 'tomatoes',
      name: 'Fresh Tomatoes',
      subtitle: 'Hybrid & Desi Varieties',
      image: 'https://images.pexels.com/photos/1327838/pexels-photo-1327838.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹30-50',
      unit: 'per kg',
      minOrder: '200 kg',
      season: 'Year-round',
      origin: 'Nashik, Maharashtra',
      organic: true,
      grade: 'Premium',
      shelfLife: '7-10 days',
      description: 'Farm-fresh tomatoes with vibrant color and firm texture. Available in both hybrid and indigenous varieties for different market needs.',
      features: ['Firm texture', 'Vibrant color', 'Low pesticide', 'Good for processing'],
      farmer: {
        name: 'Santosh Patil',
        location: 'Nashik, Maharashtra',
        image: 'https://images.pexels.com/photos/5717546/pexels-photo-5717546.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '15 years',
        farmSize: '30 acres',
        story: 'We use drip irrigation and organic practices to grow tomatoes that are both healthy and flavorful.'
      },
      certifications: ['Organic India', 'FSSAI']
    },
    {
      id: 'carrots',
      name: 'Fresh Carrots',
      subtitle: 'Orange & Red Varieties',
      image: 'https://images.pexels.com/photos/143133/pexels-photo-143133.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹35-45',
      unit: 'per kg',
      minOrder: '200 kg',
      season: 'October - March',
      origin: 'Ooty, Tamil Nadu',
      organic: true,
      grade: 'Export Grade',
      shelfLife: '2-3 weeks',
      description: 'Crunchy, sweet carrots from the hills of Ooty. Rich in beta-carotene and perfect for salads, juices, and cooking.',
      features: ['High beta-carotene', 'Sweet taste', 'Crunchy texture', 'Export quality'],
      farmer: {
        name: 'Murugan K',
        location: 'Ooty, Tamil Nadu',
        image: 'https://images.pexels.com/photos/5704849/pexels-photo-5704849.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '25 years',
        farmSize: '15 acres',
        story: 'The cool climate and rich soil of the Nilgiris give our carrots their exceptional sweetness and color.'
      },
      certifications: ['Organic Certified', 'APEDA']
    },
    {
      id: 'capsicum',
      name: 'Bell Peppers',
      subtitle: 'Red, Yellow & Green',
      image: 'https://images.pexels.com/photos/594137/pexels-photo-594137.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹80-120',
      unit: 'per kg',
      minOrder: '100 kg',
      season: 'Year-round (Polyhouse)',
      origin: 'Pune, Maharashtra',
      organic: false,
      grade: 'Premium',
      shelfLife: '10-14 days',
      description: 'Colorful bell peppers grown in controlled polyhouse conditions. Consistent quality and availability throughout the year.',
      features: ['Polyhouse grown', 'Consistent quality', 'All colors available', 'HORECA grade'],
      farmer: {
        name: 'Vinod Sharma',
        location: 'Pune, Maharashtra',
        image: 'https://images.pexels.com/photos/5717628/pexels-photo-5717628.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '12 years',
        farmSize: '5 acres polyhouse',
        story: 'Our state-of-the-art polyhouse facility produces premium bell peppers for the hospitality industry year-round.'
      },
      certifications: ['GlobalGAP', 'FSSAI']
    },
    {
      id: 'cabbage',
      name: 'Fresh Cabbage',
      subtitle: 'Green & Purple Varieties',
      image: 'https://images.pexels.com/photos/2518893/pexels-photo-2518893.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹15-25',
      unit: 'per kg',
      minOrder: '500 kg',
      season: 'October - April',
      origin: 'Shimla, Himachal Pradesh',
      organic: true,
      grade: 'Grade A',
      shelfLife: '2-3 weeks',
      description: 'Crisp, fresh cabbage from the hills. Tightly packed heads with excellent keeping quality.',
      features: ['Tight heads', 'Crisp leaves', 'Good shelf life', 'Processing suitable'],
      farmer: {
        name: 'Thakur Singh',
        location: 'Shimla, HP',
        image: 'https://images.pexels.com/photos/2382665/pexels-photo-2382665.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '30 years',
        farmSize: '20 acres',
        story: 'The cool mountain climate of Shimla produces cabbage that stays fresh longer and has superior taste.'
      },
      certifications: ['Organic India', 'FSSAI']
    },
    {
      id: 'cauliflower',
      name: 'Fresh Cauliflower',
      subtitle: 'Snow White Heads',
      image: 'https://images.pexels.com/photos/6316515/pexels-photo-6316515.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹30-50',
      unit: 'per kg',
      minOrder: '300 kg',
      season: 'October - March',
      origin: 'Panipat, Haryana',
      organic: false,
      grade: 'Premium',
      shelfLife: '7-10 days',
      description: 'Snow white cauliflower with compact, tight curds. Perfect for retail and food service.',
      features: ['White curds', 'Compact heads', 'No browning', 'Restaurant grade'],
      farmer: {
        name: 'Surender Singh',
        location: 'Panipat, Haryana',
        image: 'https://images.pexels.com/photos/5704720/pexels-photo-5704720.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '18 years',
        farmSize: '40 acres',
        story: 'We specialize in growing cauliflower with the whitest curds, preferred by premium restaurants and retailers.'
      },
      certifications: ['FSSAI', 'APEDA']
    },
    {
      id: 'green-chilli',
      name: 'Green Chillies',
      subtitle: 'Finger Hot & Bullet Varieties',
      image: 'https://images.pexels.com/photos/1374651/pexels-photo-1374651.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹40-70',
      unit: 'per kg',
      minOrder: '100 kg',
      season: 'Year-round',
      origin: 'Guntur, Andhra Pradesh',
      organic: false,
      grade: 'Export Grade',
      shelfLife: '5-7 days',
      description: 'Fresh green chillies from the chilli capital of India. Available in various heat levels from mild to extremely hot.',
      features: ['Multiple varieties', 'Consistent heat', 'Fresh & crisp', 'Export quality'],
      farmer: {
        name: 'Krishna Rao',
        location: 'Guntur, AP',
        image: 'https://images.pexels.com/photos/2804327/pexels-photo-2804327.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '28 years',
        farmSize: '50 acres',
        story: 'Guntur is synonymous with chillies. Our farms produce the finest green chillies that are exported to Southeast Asia and Middle East.'
      },
      certifications: ['APEDA', 'Spices Board']
    },
    {
      id: 'brinjal',
      name: 'Brinjal (Eggplant)',
      subtitle: 'Purple Long & Round Varieties',
      image: 'https://images.pexels.com/photos/5529577/pexels-photo-5529577.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹25-40',
      unit: 'per kg',
      minOrder: '200 kg',
      season: 'Year-round',
      origin: 'Maharashtra',
      organic: true,
      grade: 'Grade A',
      shelfLife: '5-7 days',
      description: 'Fresh brinjals in multiple varieties - long purple, round, white, and green. Essential vegetable for Indian cuisine.',
      features: ['Multiple varieties', 'Glossy skin', 'Firm flesh', 'Low seeds'],
      farmer: {
        name: 'Shankar Patil',
        location: 'Kolhapur, Maharashtra',
        image: 'https://images.pexels.com/photos/5717546/pexels-photo-5717546.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '22 years',
        farmSize: '25 acres',
        story: 'We grow over 5 varieties of brinjal using organic methods. Our produce is preferred by restaurants for its taste and texture.'
      },
      certifications: ['Organic India', 'FSSAI']
    },
    {
      id: 'okra',
      name: 'Okra (Bhindi)',
      subtitle: 'Tender Lady Finger',
      image: 'https://images.pexels.com/photos/5529580/pexels-photo-5529580.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹40-60',
      unit: 'per kg',
      minOrder: '150 kg',
      season: 'March - October',
      origin: 'Gujarat',
      organic: false,
      grade: 'Premium',
      shelfLife: '3-5 days',
      description: 'Tender, fresh okra perfect for Indian cooking. Young, green pods with minimal fiber and seeds.',
      features: ['Tender pods', 'Low fiber', 'Bright green', 'Premium quality'],
      farmer: {
        name: 'Jayesh Patel',
        location: 'Anand, Gujarat',
        image: 'https://images.pexels.com/photos/5704849/pexels-photo-5704849.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '20 years',
        farmSize: '35 acres',
        story: 'We harvest okra at the perfect stage of tenderness. Our daily harvesting ensures you get the freshest produce.'
      },
      certifications: ['FSSAI', 'GAP Certified']
    },
    {
      id: 'cucumber',
      name: 'Fresh Cucumber',
      subtitle: 'English & Desi Varieties',
      image: 'https://images.pexels.com/photos/2329440/pexels-photo-2329440.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹25-45',
      unit: 'per kg',
      minOrder: '200 kg',
      season: 'Year-round',
      origin: 'Karnataka',
      organic: true,
      grade: 'Grade A',
      shelfLife: '7-10 days',
      description: 'Crisp, refreshing cucumbers in both English (seedless) and traditional Indian varieties. Perfect for salads and garnishing.',
      features: ['Crisp texture', 'High water content', 'Seedless available', 'Salad grade'],
      farmer: {
        name: 'Basavaraj N',
        location: 'Belgaum, Karnataka',
        image: 'https://images.pexels.com/photos/5717628/pexels-photo-5717628.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '15 years',
        farmSize: '20 acres',
        story: 'Our polyhouse-grown English cucumbers are supplied to premium hotels and supermarkets across South India.'
      },
      certifications: ['Organic Certified', 'FSSAI']
    },
    {
      id: 'bitter-gourd',
      name: 'Bitter Gourd (Karela)',
      subtitle: 'Health Superfood',
      image: 'https://images.pexels.com/photos/5529574/pexels-photo-5529574.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹45-65',
      unit: 'per kg',
      minOrder: '100 kg',
      season: 'March - October',
      origin: 'Madhya Pradesh',
      organic: true,
      grade: 'Premium',
      shelfLife: '5-7 days',
      description: 'Fresh bitter gourd known for its health benefits, especially for diabetics. Young, green, and moderately bitter.',
      features: ['Health benefits', 'Diabetic friendly', 'Young tender', 'Medicinal value'],
      farmer: {
        name: 'Ramkumar Tiwari',
        location: 'Indore, MP',
        image: 'https://images.pexels.com/photos/2382665/pexels-photo-2382665.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '25 years',
        farmSize: '30 acres',
        story: 'We grow bitter gourd organically without any pesticides. Our karela is sought after by health-conscious consumers and Ayurvedic companies.'
      },
      certifications: ['Organic India', 'FSSAI']
    }
  ];

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/1656666/pexels-photo-1656666.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Fresh vegetables"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#263776]/95 via-[#263776]/70 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <Link to="/products/orivana-fresh" className="inline-flex items-center text-white/80 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to ORIVANA Fresh
          </Link>
          <Badge className="bg-[#F47D20] text-white mb-4 block w-fit">Farm Fresh</Badge>
          <h1 className="text-5xl font-bold text-white mb-4">Vegetables</h1>
          <p className="text-xl text-white/90 max-w-2xl">
            Fresh seasonal vegetables sourced directly from farms across India. Quality checked, cold-chain delivered.
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left: Products List */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-bold text-[#263776] mb-6">Our Vegetables</h2>
              <div className="space-y-4">
                {vegetables.map((product) => (
                  <Card 
                    key={product.id}
                    className={`cursor-pointer transition-all duration-300 ${
                      selectedProduct?.id === product.id 
                        ? 'ring-2 ring-[#F47D20] shadow-lg' 
                        : 'hover:shadow-md'
                    }`}
                    onClick={() => setSelectedProduct(product)}
                  >
                    <CardContent className="p-4 flex items-center gap-4">
                      <img 
                        src={product.image} 
                        alt={product.name}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-[#263776]">{product.name}</h3>
                          {product.organic && (
                            <Badge className="bg-green-100 text-green-700 text-xs">Organic</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">{product.origin}</p>
                        <p className="text-[#F47D20] font-bold mt-1">{product.price} <span className="text-xs text-slate-400">{product.unit}</span></p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-400" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Right: Selected Product Details */}
            <div className="lg:col-span-2">
              {selectedProduct ? (
                <div className="sticky top-4">
                  <div className="relative rounded-xl overflow-hidden h-[350px] mb-6">
                    <img 
                      src={selectedProduct.image} 
                      alt={selectedProduct.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4 flex gap-2">
                      <Badge className="bg-[#263776] text-white">{selectedProduct.grade}</Badge>
                      {selectedProduct.organic && (
                        <Badge className="bg-green-600 text-white">
                          <Leaf className="w-3 h-3 mr-1" /> Organic
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h2 className="text-3xl font-bold text-[#263776] mb-2">{selectedProduct.name}</h2>
                      <p className="text-slate-600 mb-4">{selectedProduct.description}</p>
                      
                      <div className="bg-slate-50 rounded-lg p-4 mb-4">
                        <h4 className="font-bold text-[#263776] mb-3 flex items-center">
                          <Info className="w-4 h-4 mr-2" /> Specifications
                        </h4>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div><span className="text-slate-500">Season:</span> <span className="font-medium">{selectedProduct.season}</span></div>
                          <div><span className="text-slate-500">Origin:</span> <span className="font-medium">{selectedProduct.origin}</span></div>
                          <div><span className="text-slate-500">Shelf Life:</span> <span className="font-medium">{selectedProduct.shelfLife}</span></div>
                          <div><span className="text-slate-500">Min Order:</span> <span className="font-medium">{selectedProduct.minOrder}</span></div>
                        </div>
                      </div>

                      <div className="mb-4">
                        <h4 className="font-bold text-[#263776] mb-2">Key Features</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedProduct.features.map((feature, idx) => (
                            <Badge key={idx} variant="outline" className="border-[#263776]/30">
                              {feature}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div>
                      <Card className="mb-6 border-2 border-[#F47D20]/20">
                        <CardContent className="p-6">
                          <div className="text-3xl font-bold text-[#F47D20] mb-1">{selectedProduct.price}</div>
                          <div className="text-slate-500 mb-4">{selectedProduct.unit} (Wholesale)</div>
                          <Button asChild className="w-full bg-[#F47D20] hover:bg-[#d66b1a] mb-3">
                            <Link to={`/products/orivana-fresh/enquiry?product=${selectedProduct.id}&category=vegetables`}>
                              <ShoppingCart className="w-4 h-4 mr-2" />
                              Request Quote
                            </Link>
                          </Button>
                        </CardContent>
                      </Card>

                      <Card className="bg-gradient-to-br from-[#263776]/5 to-[#F47D20]/5">
                        <CardContent className="p-6">
                          <h4 className="font-bold text-[#263776] mb-4 flex items-center">
                            <User className="w-4 h-4 mr-2" /> Meet the Farmer
                          </h4>
                          <div className="flex items-start gap-4">
                            <img 
                              src={selectedProduct.farmer.image}
                              alt={selectedProduct.farmer.name}
                              className="w-20 h-20 rounded-full object-cover"
                            />
                            <div>
                              <h5 className="font-bold text-[#263776]">{selectedProduct.farmer.name}</h5>
                              <div className="flex items-center text-sm text-slate-500 mb-2">
                                <MapPin className="w-3 h-3 mr-1" />
                                {selectedProduct.farmer.location}
                              </div>
                            </div>
                          </div>
                          <p className="text-sm text-slate-600 mt-4 italic">
                            "{selectedProduct.farmer.story}"
                          </p>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-[500px] bg-slate-50 rounded-xl">
                  <div className="text-center">
                    <div className="w-20 h-20 bg-[#263776]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <ArrowLeft className="w-8 h-8 text-[#263776]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#263776] mb-2">Select a Product</h3>
                    <p className="text-slate-500">Click on a vegetable to view details</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-[#263776]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Need Regular Vegetable Supply?</h2>
          <p className="text-white/80 mb-6">Set up weekly or monthly delivery schedules for your business</p>
          <Button asChild size="lg" className="bg-[#F47D20] hover:bg-[#d66b1a]">
            <Link to="/products/orivana-fresh/enquiry">Get Custom Quote</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default VegetablesCatalogue;
